import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { Button } from 'react-bootstrap';
import Flex from '../Flex';

type AdvanceTablePaginationProps = {
  /**
   * Marker prop used by AdvanceTableWrapper to detect this child and inject the
   * react-table pagination state below via cloneElement. The remaining props are
   * optional because the wrapper supplies them at runtime, not the caller.
   */
  table?: boolean;
  canPreviousPage?: boolean;
  canNextPage?: boolean;
  previousPage?: () => void;
  nextPage?: () => void;
  pageCount?: number;
  pageIndex?: number;
  gotoPage?: (page: number) => void;
};

export const AdvanceTablePagination = ({
  canPreviousPage = false,
  canNextPage = false,
  previousPage = () => {},
  nextPage = () => {},
  pageCount = 0,
  pageIndex = 0,
  gotoPage = () => {},
}: AdvanceTablePaginationProps) => {
  const visiblePages = 6; // max number of page buttons shown

  // Work entirely in 0-based page indexes, then render as 1-based labels.
  let startPage = Math.max(pageIndex - Math.floor(visiblePages / 2), 0);
  let endPage = Math.min(startPage + visiblePages - 1, pageCount - 1);
  startPage = Math.max(endPage - visiblePages + 1, 0);

  const pages = [];
  for (let page = startPage; page <= endPage; page++) {
    pages.push(page);
  }

  return (
    <Flex alignItems="center" justifyContent="center">
      <Button
        size="sm"
        variant="falcon-default"
        onClick={() => gotoPage(0)}
        disabled={!canPreviousPage}
        className="me-2"
      >
        <FontAwesomeIcon icon="angle-double-left" />
      </Button>
      <Button
        size="sm"
        variant="falcon-default"
        onClick={() => previousPage()}
        disabled={!canPreviousPage}
      >
        <FontAwesomeIcon icon="chevron-left" />
      </Button>
      <ul className="pagination mb-0 mx-2 d-flex">
        {pages.map((page) => (
          <li key={page} className={classNames({ active: pageIndex === page })}>
            <Button
              size="sm"
              variant={pageIndex === page ? 'primary' : 'falcon-default'}
              className={classNames('page', {
                'me-2': page !== pageCount - 1,
              })}
              onClick={() => gotoPage(page)}
            >
              {page + 1}
            </Button>
          </li>
        ))}
      </ul>
      <Button
        size="sm"
        variant="falcon-default"
        onClick={() => nextPage()}
        disabled={!canNextPage}
      >
        <FontAwesomeIcon icon="chevron-right" />
      </Button>
      <Button
        size="sm"
        variant="falcon-default"
        onClick={() => gotoPage(pageCount - 1)}
        disabled={!canNextPage}
        className="ms-2"
      >
        <FontAwesomeIcon icon="angle-double-right" />
      </Button>
    </Flex>
  );
};

export default AdvanceTablePagination;
